import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { routes } from '../../constants';
import {
  Box,
  Button,
  Divider,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from '@mui/material';
import StyledPage from '../../components/StyledPage';
import api from '../../api';

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const uid = searchParams.get('uid');
  const token = searchParams.get('token');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // <600px

  const defaultForm = {
    new_password: '',
    confirm_password: '',
  };

  const [success, setSuccess] = useState(false);
  const [userData, setUserData] = useState(defaultForm);
  const [inputErrors, setInputErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false); // Loading state

  const modifyFormData = (property, event) => {
    const dataCopy = { ...userData };
    dataCopy[property] = event.target.value;
    setUserData(dataCopy);
  };

  const sendNewPassword = async (event) => {
    event.preventDefault(); // Prevent form submission from refreshing the page

    // Client-side validation
    let errors = {};
    if (userData.new_password.trim() === '') {
      errors.new_password = <FormattedMessage id="register.password_required" />;
    } else if (userData.new_password.length < 8) {
      errors.new_password = <FormattedMessage id="register.password_min_length" />;
    }
    if (userData.confirm_password.trim() === '') {
      errors.confirm_password = <FormattedMessage id="register.confirm_password_required" />;
    }
    if (userData.new_password !== userData.confirm_password) {
      errors.confirm_password = <FormattedMessage id="register.passwords_mismatch" />;
    }
    if (Object.keys(errors).length !== 0) {
      setInputErrors(errors);
      return;
    }

    setIsLoading(true);
    try {
      const resetStatus = await api.resetPassword(uid, token, userData);
      if (Object.keys(resetStatus.data).includes('message')) {
        if (resetStatus.data['message'] === 'Password reset successful.') {
          setSuccess(true);
        }
      } else {
        setInputErrors({
          error: resetStatus.data.error || <FormattedMessage id="reset_password.error" />,
        });
      }
    } catch (error) {
      setInputErrors({
        error: <FormattedMessage id="reset_password.error" />,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <StyledPage
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: isMobile ? 'center' : 'space-between',
        alignItems: 'center',
        background: isMobile
          ? '#f5f5f5'
          : `url("https://images.unsplash.com/photo-1507521628349-dee6b8290f6f")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      {/* Form Section */}
      <Box
        sx={{
          flex: isMobile ? 'auto' : 1,
          p: { xs: 2, md: 5 },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: isMobile ? 'transparent' : 'rgba(255, 255, 255, 0.9)',
          backdropFilter: isMobile ? 'none' : 'blur(5px)',
          borderRadius: 1,
          maxWidth: { xs: '100%', md: '500px' },
          mx: 'auto',
          my: isMobile ? 0 : { xs: 1, md: 0 },
          boxShadow: isMobile ? '0px 2px 4px rgba(0, 0, 0, 0.1)' : 0,
          transform: isMobile ? 'translateY(-10%)' : 'none',
        }}
      >
        {isMobile && (
          <Typography
            variant="h5"
            sx={{
              mb: 3,
              color: '#00438E',
              textAlign: 'center',
              fontWeight: 'bold',
            }}
          >
            PromptaAI
          </Typography>
        )}
        {success ? (
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h6"
              sx={{
                mb: 3,
                color: '#00438E',
                fontWeight: 'bold',
              }}
            >
              <FormattedMessage id="register.success_header" />
            </Typography>
            <Typography
              sx={{
                mb: 3,
                color: '#00438E',
                fontSize: 16,
              }}
            >
              <FormattedMessage id="register.password_r_reset_success" />
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate(routes.login)}
              sx={{
                width: isMobile ? '100%' : 'auto',
                backgroundColor: '#00438E',
                '&:hover': { backgroundColor: '#003A75' },
                py: 1.5,
                px: 3,
              }}
            >
              <FormattedMessage id="login.return_to_login" />
            </Button>
          </Box>
        ) : (
          <Box component="form" onSubmit={sendNewPassword} sx={{ width: '100%' }}>
            <Typography
              variant="h6"
              sx={{
                mb: 3,
                color: '#00438E',
                textAlign: 'center',
                fontWeight: 'bold',
              }}
            >
              <FormattedMessage id="login.reset_password" />
            </Typography>
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                error={Object.keys(inputErrors).includes('new_password')}
                helperText={inputErrors['new_password'] || ''}
                slotProps={{ inputLabel: { shrink: true } }}
                size="small"
                type="password"
                label={<FormattedMessage id="register.password_r" />}
                value={userData.new_password}
                onChange={(event) => modifyFormData('new_password', event)}
                sx={{
                  '& .MuiInputBase-root': { backgroundColor: '#f0f0f0', borderRadius: 1 },
                  '& .MuiInputLabel-root': { color: '#00438E' },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#00438E' },
                    '&:hover fieldset': { borderColor: '#4A87C8' },
                    '&.Mui-focused fieldset': { borderColor: '#4A87C8' },
                  },
                }}
              />
            </Box>
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                error={Object.keys(inputErrors).includes('confirm_password')}
                helperText={inputErrors['confirm_password'] || ''}
                slotProps={{ inputLabel: { shrink: true } }}
                size="small"
                type="password"
                label={<FormattedMessage id="register.confirm_password_r" />}
                value={userData.confirm_password}
                onChange={(event) => modifyFormData('confirm_password', event)}
                sx={{
                  '& .MuiInputBase-root': { backgroundColor: '#f0f0f0', borderRadius: 1 },
                  '& .MuiInputLabel-root': { color: '#00438E' },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#00438E' },
                    '&:hover fieldset': { borderColor: '#4A87C8' },
                    '&.Mui-focused fieldset': { borderColor: '#4A87C8' },
                  },
                }}
              />
            </Box>
            <Box sx={{ color: 'crimson', mb: 2, textAlign: 'center' }}>
              {Object.keys(inputErrors).includes('error') ? inputErrors['error'] : ''}
            </Box>
            <Divider sx={{ my: 2 }} />
            <Button
              size="large"
              type="submit"
              variant="contained"
              disabled={isLoading}
              sx={{
                width: '100%',
                backgroundColor: '#00438E',
                '&:hover': { backgroundColor: '#003A75' },
                py: 1.5,
              }}
            >
              {isLoading ? (
                <CircularProgress size={24} sx={{ color: '#ffffff' }} />
              ) : (
                <FormattedMessage id="general.submit" />
              )}
            </Button>
          </Box>
        )}
      </Box>
    </StyledPage>
  );
}